import React, { useEffect, useState } from "react";
import Dialog from "@mui/material/Dialog";
import DialogTitle from "@mui/material/DialogTitle";
import DialogContent from "@mui/material/DialogContent";
import DialogActions from "@mui/material/DialogActions";
import Button from "@mui/material/Button";
import Box from "@mui/material/Box";
import Typography from "@mui/material/Typography";
import CircularProgress from "@mui/material/CircularProgress";
import TextField from "@mui/material/TextField";
import IconButton from "@mui/material/IconButton";
import EditIcon from "@mui/icons-material/Edit";
import DeleteIcon from "@mui/icons-material/Delete";
import {
  ChartSupporting,
  apiCreateChartSupporting,
  apiDeleteChartSupporting,
  apiGetChartSupportings,
  apiUpdateChartSupporting,
} from "../../../services/ChartSupportingAPI";

export default function SupportModal(props: {
  open: boolean;
  chartId: number;
  onClose: () => void;
}) {
  const { open, chartId, onClose } = props;

  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | undefined>(undefined);
  const [supportings, setSupportings] = useState<ChartSupporting[]>([]);
  const [textValue, setTextValue] = useState("");
  const [saving, setSaving] = useState(false);
  const [editingId, setEditingId] = useState<number | null>(null);
  const [deletingId, setDeletingId] = useState<number | null>(null);
  const [expandedId, setExpandedId] = useState<number | null>(null);

  useEffect(() => {
    if (!open) return;

    let cancelled = false;
    setLoading(true);
    setError(undefined);

    apiGetChartSupportings(chartId)
      .then((response) => {
        if (cancelled) return;
        const data = (response.data?.chart_supportings ||
          response.data?.chart_supporting ||
          response.data) as ChartSupporting[] | undefined;
        setSupportings(data || []);
        setLoading(false);
      })
      .catch((e) => {
        if (cancelled) return;
        setError(String(e));
        setLoading(false);
      });

    return () => {
      cancelled = true;
    };
  }, [open, chartId]);

  function resetEditor() {
    setTextValue("");
    setEditingId(null);
    setSaving(false);
  }

  async function handleSave() {
    if (!textValue.trim()) return;
    try {
      setSaving(true);
      if (editingId === null) {
        await apiCreateChartSupporting(chartId, textValue);
      } else {
        await apiUpdateChartSupporting(chartId, editingId, textValue);
      }
      const response = await apiGetChartSupportings(chartId);
      const data = (response.data?.chart_supportings ||
        response.data?.chart_supporting ||
        response.data) as ChartSupporting[] | undefined;
      setSupportings(data || []);
      resetEditor();
      setError(undefined);
    } catch (e) {
      setError(String(e));
    } finally {
      setSaving(false);
    }
  }

  async function handleEdit(s: ChartSupporting) {
    setEditingId(s.id);
    setTextValue(s.data || "");
  }

  async function handleDelete(id: number) {
    try {
      setDeletingId(id);
      await apiDeleteChartSupporting(chartId, id);
      const response = await apiGetChartSupportings(chartId);
      const data = (response.data?.chart_supportings ||
        response.data?.chart_supporting ||
        response.data) as ChartSupporting[] | undefined;
      setSupportings(data || []);
      setError(undefined);
    } catch (e) {
      setError(String(e));
    } finally {
      setDeletingId(null);
    }
  }

  function getSummary(text: string): string {
    const lines = text.split("\n");
    if (lines.length <= 3) return text;
    return lines.slice(0, 3).join("\n") + "\n...";
  }

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle>Supporting documents</DialogTitle>
      <DialogContent dividers>
        {loading && (
          <Box sx={{ display: "flex", justifyContent: "center", py: 4 }}>
            <CircularProgress />
          </Box>
        )}
        {!loading && error && (
          <Typography color="error">
            Failed to load supporting documents: {error}
          </Typography>
        )}
        {!loading && !error && (
          <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
            {supportings.length === 0 && (
              <Typography color="text.secondary">
                No supporting documents found for this chart.
              </Typography>
            )}
            {supportings.length > 0 && (
              <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
                {supportings.map((s) => (
                  <Box
                    key={s.id}
                    sx={{
                      border: "1px solid",
                      borderColor: "divider",
                      borderRadius: 1,
                      p: 1.5,
                      bgcolor: "background.paper",
                    }}
                  >
                    <Box
                      sx={{
                        display: "flex",
                        justifyContent: "space-between",
                        alignItems: "center",
                        mb: 1,
                      }}
                    >
                      <Typography variant="caption" color="text.secondary">
                        Created: {s.created_at}
                        {s.updated_at && s.updated_at !== s.created_at && (
                          <> | Updated: {s.updated_at}</>
                        )}
                      </Typography>
                      <Box>
                        <IconButton
                          size="small"
                          onClick={() => handleEdit(s)}
                          title="Edit supporting document"
                        >
                          <EditIcon fontSize="small" />
                        </IconButton>
                        <IconButton
                          size="small"
                          onClick={() => handleDelete(s.id)}
                          title="Delete supporting document"
                          disabled={deletingId === s.id}
                        >
                          <DeleteIcon fontSize="small" />
                        </IconButton>
                      </Box>
                    </Box>
                    <Box sx={{ mt: 1 }}>
                      <Box
                        component="pre"
                        sx={{
                          whiteSpace: "pre-wrap",
                          wordBreak: "break-word",
                          fontFamily: "monospace",
                          fontSize: "0.85rem",
                          bgcolor: "grey.100",
                          p: 1,
                          borderRadius: 1,
                          maxHeight: expandedId === s.id ? "none" : 80,
                          overflow: "hidden",
                        }}
                      >
                        {expandedId === s.id ? s.data : getSummary(s.data)}
                      </Box>
                      {s.data.split("\n").length > 3 && (
                        <Button
                          size="small"
                          sx={{ mt: 0.5 }}
                          onClick={() =>
                            setExpandedId(expandedId === s.id ? null : s.id)
                          }
                        >
                          {expandedId === s.id ? "Hide" : "View"}
                        </Button>
                      )}
                    </Box>
                  </Box>
                ))}
              </Box>
            )}
            <Box sx={{ mt: 3 }}>
              <Typography variant="subtitle2" sx={{ mb: 1 }}>
                {editingId === null ? "Add supporting document" : "Edit supporting document"}
              </Typography>
              <TextField
                value={textValue}
                onChange={(e) => setTextValue(e.target.value)}
                multiline
                minRows={4}
                fullWidth
                placeholder="Enter supporting text..."
              />
            </Box>
          </Box>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Close</Button>
        <Button
          onClick={resetEditor}
          disabled={saving && !editingId}
        >
          Clear
        </Button>
        <Button
          variant="contained"
          onClick={handleSave}
          disabled={saving || !textValue.trim()}
        >
          {editingId === null ? "Create" : "Update"}
        </Button>
      </DialogActions>
    </Dialog>
  );
}

